import type { CurrentPriceDto } from "./dto";

/** A product_prices row, reduced to what the resolution logic needs. */
export interface PriceRow {
  id: string;
  customerId: string | null; // null = list price
  price: number; // minor units
  currency: string;
  effectiveFrom: number;
  effectiveTo: number | null; // null = open
}

function effectiveAt(r: PriceRow, asOf: number): boolean {
  return r.effectiveFrom <= asOf && (r.effectiveTo == null || r.effectiveTo > asOf);
}

/** Deterministic latest: newest effectiveFrom, then id desc as a stable tiebreak. */
function latest(rows: PriceRow[]): PriceRow | undefined {
  return rows
    .slice()
    .sort((a, b) => b.effectiveFrom - a.effectiveFrom || (a.id < b.id ? 1 : -1))[0];
}

/**
 * Resolve the effective price for a (product, customer) pair at `asOf`.
 * A customer-specific open row wins over the list price; future/closed rows are
 * ignored; with no applicable row the source is "none" (caller falls back to the
 * product's own selling price).
 */
export function pickEffective(
  rows: PriceRow[],
  opts: { customerId?: string | null; asOf: number; fallbackCurrency: string },
): CurrentPriceDto {
  if (opts.customerId != null) {
    const cust = latest(rows.filter((r) => r.customerId === opts.customerId && effectiveAt(r, opts.asOf)));
    if (cust) return { price: cust.price, currency: cust.currency, source: "customer" };
  }
  const list = latest(rows.filter((r) => r.customerId === null && effectiveAt(r, opts.asOf)));
  if (list) return { price: list.price, currency: list.currency, source: "list" };
  return { price: null, currency: opts.fallbackCurrency, source: "none" };
}


/** Một bậc giá: áp dụng từ `fromQuantity` trở lên, tính theo đơn vị gốc. */
export interface PriceTier { fromQuantity: number; price: number; currency?: string }

export type SellingPriceSource = "customer" | "underMoq" | "tier" | "list" | "product" | "none";

export interface SellingPriceResult {
  price: number | null;
  currency: string;
  source: SellingPriceSource;
  /** Mốc của bậc đã dùng — để màn hình nói rõ "đang áp bậc từ 101". */
  tierFrom?: number;
}

/**
 * Giá bán gợi ý cho một dòng chứng từ.
 *
 * THỨ TỰ ƯU TIÊN, và lý do:
 *  1. Giá riêng của khách. Đó là THỎA THUẬN với đúng khách đó; bảng bậc là giá
 *     chung, để giá chung đè lên thỏa thuận riêng là tự phá cam kết.
 *  2. Số lượng dưới MOQ và có giá dưới MOQ → lấy giá dưới MOQ. Bậc thang là giá
 *     sỉ khi đã đạt MOQ; đơn nhỏ đi đường riêng.
 *  3. Bậc theo số lượng: mốc lớn nhất còn <= số lượng.
 *  4. Bảng giá chung (product_prices không gắn khách).
 *  5. Giá bán trên thẻ sản phẩm.
 *
 * Đây là GỢI Ý. Người lập chứng từ vẫn sửa được; hàm này không quyết định giá
 * cuối cùng, chỉ nói giá nào hợp lý nhất theo dữ liệu đang có.
 *
 * `quantity` phải quy về ĐƠN VỊ GỐC trước khi gọi. Không biết số lượng thì bỏ
 * qua bước 2 và 3 thay vì đoán bừa một bậc.
 */
export function resolveSellingPrice(input: {
  rows: PriceRow[];
  tiers: PriceTier[];
  quantity: number | null;
  moq: number | null;
  underMoqPrice: number | null;
  productPrice: number | null;
  customerId?: string | null;
  asOf: number;
  fallbackCurrency: string;
}): SellingPriceResult {
  const { fallbackCurrency } = input;
  if (input.customerId != null) {
    const cust = latest(input.rows.filter((r) => r.customerId === input.customerId && effectiveAt(r, input.asOf)));
    if (cust) return { price: cust.price, currency: cust.currency, source: "customer" };
  }
  const qty = input.quantity;
  if (qty != null && qty > 0) {
    if (input.moq != null && input.moq > 0 && qty < input.moq && input.underMoqPrice != null)
      return { price: input.underMoqPrice, currency: fallbackCurrency, source: "underMoq" };
    const tier = input.tiers
      .filter((t) => t.fromQuantity <= qty)
      .sort((a, b) => b.fromQuantity - a.fromQuantity)[0];
    if (tier)
      return { price: tier.price, currency: tier.currency ?? fallbackCurrency, source: "tier", tierFrom: tier.fromQuantity };
  }
  const list = latest(input.rows.filter((r) => r.customerId === null && effectiveAt(r, input.asOf)));
  if (list) return { price: list.price, currency: list.currency, source: "list" };
  if (input.productPrice != null) return { price: input.productPrice, currency: fallbackCurrency, source: "product" };
  return { price: null, currency: fallbackCurrency, source: "none" };
}
